/**
 * What the API actually returns when something goes wrong, and how it becomes a sentence.
 *
 * The service has one envelope for every deliberate error — `app/core/exceptions.py`:
 *
 *     { "error": { "code": "conflict", "message": "an account with that email already exists" } }
 *
 * and a second shape it does not control, FastAPI's own 422 for a payload that fails validation:
 *
 *     { "detail": [ { "loc": ["body", "password"], "msg": "String should have at least 12 …" } ] }
 *
 * Both are surfaced. The service writes deliberately plain messages — "email or password is
 * incorrect", "an account with that email already exists" — and they are better than anything a
 * client could invent, so the message is shown as written rather than mapped to a house string.
 * Only two cases get client-side wording: a transport failure, where there is no body to read,
 * and a 500, where the body is an internal detail nobody outside the service should read.
 */

/** The machine-readable codes this client reasons about. Anything else falls through as-is. */
export type ApiErrorCode =
  | "unauthenticated"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "quota_exceeded"
  | "validation_error"
  | "rate_limited"
  | "index_unavailable"
  | "internal_error"
  /** No HTTP response at all: DNS, TLS, offline, CORS. Client-side, never from the server. */
  | "network_error";

export class ApiError extends Error {
  readonly code: ApiErrorCode | string;
  readonly status: number;
  /** Which limit was hit, on a `quota_exceeded`. The service sends it so a UI need not parse prose. */
  readonly limit?: string;
  /** Seconds to wait, from `Retry-After` on a 429. */
  readonly retryAfter?: number;

  constructor(
    message: string,
    options: { code: ApiErrorCode | string; status: number; limit?: string; retryAfter?: number },
  ) {
    super(message);
    this.name = "ApiError";
    this.code = options.code;
    this.status = options.status;
    this.limit = options.limit;
    this.retryAfter = options.retryAfter;
  }

  /** True when signing in again is the thing that would fix it. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.code === "unauthenticated" || this.code === "unauthorized";
  }
}

export function networkError(cause?: unknown): ApiError {
  const error = new ApiError(
    "Could not reach Athena. Check your connection and try again.",
    { code: "network_error", status: 0 },
  );
  if (cause !== undefined) error.cause = cause;
  return error;
}

interface ValidationDetail {
  loc?: unknown;
  msg?: unknown;
}

/** FastAPI's 422 body, flattened to one readable sentence naming the field that failed. */
function fromValidationDetail(detail: ValidationDetail[]): string | null {
  const parts = detail
    .map((item) => {
      const msg = typeof item.msg === "string" ? item.msg : null;
      if (!msg) return null;
      const loc = Array.isArray(item.loc) ? item.loc : [];
      // Drop the leading "body"/"query" — it names the envelope, not the field.
      const field = loc.filter((s) => typeof s === "string" && s !== "body" && s !== "query");
      return field.length > 0 ? `${field.join(".")}: ${msg}` : msg;
    })
    .filter((s): s is string => s !== null);
  return parts.length > 0 ? parts.join("; ") : null;
}

/**
 * Turn a non-2xx `Response` into an `ApiError`. Reads the body once; a body that is not JSON, or
 * is JSON in neither shape, still produces something a person can act on.
 */
export async function errorFromResponse(response: Response): Promise<ApiError> {
  const retryAfterHeader = response.headers.get("Retry-After");
  const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : undefined;

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (body !== null && typeof body === "object") {
    const envelope = (body as { error?: unknown }).error;
    if (envelope !== null && typeof envelope === "object") {
      const { code, message, limit } = envelope as Record<string, unknown>;
      if (typeof message === "string" && message.length > 0) {
        return new ApiError(message, {
          code: typeof code === "string" ? code : "internal_error",
          status: response.status,
          limit: typeof limit === "string" ? limit : undefined,
          retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
        });
      }
    }

    const detail = (body as { detail?: unknown }).detail;
    if (Array.isArray(detail)) {
      const message = fromValidationDetail(detail as ValidationDetail[]);
      if (message) {
        return new ApiError(message, { code: "validation_error", status: response.status });
      }
    }
    if (typeof detail === "string" && detail.length > 0) {
      return new ApiError(detail, { code: "internal_error", status: response.status });
    }
  }

  // No usable body. A 5xx body would be an internal detail anyway.
  if (response.status >= 500) {
    return new ApiError("Athena is having trouble right now. Try again in a moment.", {
      code: "internal_error",
      status: response.status,
    });
  }
  return new ApiError(`Request failed (${response.status}).`, {
    code: "internal_error",
    status: response.status,
  });
}
